import { useState, useEffect, useRef } from 'react';
import {
  Users, FileText, Calendar, DollarSign, BarChart3,
  ArrowRight, Star, Zap, Smartphone, MapPin, Clock,
  CheckCircle, Wrench, Flame, Shield
} from 'lucide-react';

export default function LandingPage({ onLogin, onSignup }) {
  const [currentTrade, setCurrentTrade] = useState(0);

  // Logo splash on first visit. Once shown in a session, skip on
  // subsequent navigations so it doesn't replay every page hit.
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('wv_landing_splash_shown') !== '1';
  });
  const [splashFading, setSplashFading] = useState(false);
  const splashVideoRef = useRef(null);

  useEffect(() => {
    if (!showSplash) return;
    sessionStorage.setItem('wv_landing_splash_shown', '1');
    // Safety fallback in case the video doesn't fire onEnded
    const fallback = setTimeout(() => {
      setSplashFading(true);
      setTimeout(() => setShowSplash(false), 600);
    }, 4500);
    return () => clearTimeout(fallback);
  }, [showSplash]);

  const handleSplashEnd = () => {
    setSplashFading(true);
    setTimeout(() => setShowSplash(false), 600);
  };

  // Mobile responsiveness - hide WorkVan text on small screens
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 480px) {
        .workvan-logo-text { display: none !important; }
        .nav-btn { padding: 0.4rem 0.75rem !important; font-size: 0.85rem !important; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const trades = [
    'Garage Door',
    'HVAC',
    'Plumbing',
    'Electrical',
    'Pest Control',
    'Carpet Cleaning',
    'Pressure Washing',
    'Mobile Detailing'
  ];

  // Rotate through trades
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTrade(prev => (prev + 1) % trades.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Capture affiliate referral code and test mode flag from URL on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');

    if (ref) {
      const existingRef = localStorage.getItem('affiliate_ref');
      if (!existingRef) {
        localStorage.setItem('affiliate_ref', ref);
        localStorage.setItem('affiliate_ref_captured_at', new Date().toISOString());
        document.cookie = `affiliate_ref=${ref}; max-age=${60 * 24 * 60 * 60}; path=/; SameSite=Lax`;
      }
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }

    // Capture test mode flag (used by internal test accounts system)
    const testParam = params.get('test');
    if (testParam === '1') {
      localStorage.setItem('test_mode', 'true');
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Customers & Leads',
      description: 'Never lose a lead. Track every call, every customer, every opportunity.'
    },
    {
      icon: FileText,
      title: 'Quick Quotes',
      description: 'Build professional quotes in minutes. Digital signatures included.'
    },
    {
      icon: Calendar,
      title: 'Smart Scheduling',
      description: 'Drag-and-drop calendar. Assign techs. Send confirmations automatically.'
    },
    {
      icon: Zap,
      title: '30-Second Jobs',
      description: 'On-site? Create a complete job in 30 seconds. Customer, details, done.'
    },
    {
      icon: DollarSign,
      title: 'Invoicing',
      description: 'Auto-generate invoices from completed jobs. Track who owes you.'
    },
    {
      icon: BarChart3,
      title: 'Know Your Numbers',
      description: 'See your revenue, track your best days, know your average ticket.'
    }
  ];

  const painPoints = [
    { problem: 'Sticky notes everywhere', solution: 'Everything in one app' },
    { problem: 'Forgot to follow up', solution: 'Never miss a lead again' },
    { problem: 'Handwritten quotes look unprofessional', solution: 'Clean digital quotes in minutes' },
    { problem: '"Who owes me money?"', solution: 'See outstanding invoices instantly' },
    { problem: 'Scheduling chaos', solution: 'Visual calendar with drag-and-drop' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      color: '#e0e0e0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Logo splash — first visit only this session */}
      {showSplash && (
        <div
          onClick={handleSplashEnd}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: splashFading ? 0 : 1,
            transition: 'opacity 0.6s ease-out',
            cursor: 'pointer',
          }}
        >
          <video
            ref={splashVideoRef}
            src="/logo-animation.mp4"
            autoPlay
            muted
            playsInline
            onEnded={handleSplashEnd}
            style={{
              width: '100vw',
              height: '100vh',
              objectFit: 'contain',
            }}
          />
        </div>
      )}

      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: 'rgba(10, 10, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #1a1a1a',
        zIndex: 1000,
        padding: '0.75rem 1rem',
        paddingTop: 'max(0.75rem, env(safe-area-inset-top))'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.625rem'
          }}>
            <img
              src="/WorkVan-logo-dark.png"
              alt="Work Van"
              style={{
                height: '36px',
                width: 'auto'
              }}
            />
            <span className="workvan-logo-text" style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#F05A28'
            }}>
              WorkVan
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="nav-btn"
              onClick={onLogin}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#4ecca3',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600'
              }}
            >
              Log In
            </button>
            <button
              className="nav-btn"
              onClick={onSignup}
              style={{
                background: '#F05A28',
                border: 'none',
                color: '#fff',
                padding: '0.625rem 1.25rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '700'
              }}
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        paddingTop: '120px',
        paddingBottom: '80px',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #111 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Subtle gradient orb */}
        <div style={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(240, 90, 40, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '0 1.5rem',
          textAlign: 'center',
          position: 'relative'
        }}>
          {/* Logo */}
          <img
            src="/WorkVan-logo-dark.png"
            alt="Work Van"
            style={{
              height: 'clamp(80px, 18vw, 160px)',
              width: 'auto',
              marginBottom: '2rem'
            }}
          />

          {/* Main Headline */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
            fontWeight: '800',
            lineHeight: '1.1',
            marginBottom: '1.5rem',
            color: '#fff'
          }}>
            Run Your{' '}
            <span style={{
              color: '#F05A28',
              display: 'inline-block',
              minWidth: '280px'
            }}>
              {trades[currentTrade]}
            </span>
            <br />
            Business Smarter
          </h1>

          <p style={{
            fontSize: 'clamp(1.1rem, 2.5vw, 1.35rem)',
            color: '#888',
            maxWidth: '600px',
            margin: '0 auto 2.5rem',
            lineHeight: '1.6'
          }}>
            Customers. Quotes. Scheduling. Invoices.<br />
            One app. No spreadsheets. No chaos.
          </p>

          {/* CTA Buttons */}
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            marginBottom: '2rem'
          }}>
            <button
              onClick={onSignup}
              style={{
                background: '#F05A28',
                border: 'none',
                color: '#fff',
                padding: '1rem 2rem',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 24px rgba(240, 90, 40, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Start Free Trial
              <ArrowRight size={20} />
            </button>
          </div>

          <p style={{
            fontSize: '0.9rem',
            color: '#666'
          }}>
            Free 14-day trial &bull; No credit card required
          </p>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section style={{
        background: '#111',
        borderTop: '1px solid #1a1a1a',
        borderBottom: '1px solid #1a1a1a',
        padding: '1.5rem 2rem'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '2rem',
          flexWrap: 'wrap',
          color: '#666',
          fontSize: '0.9rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Shield size={18} color="#4ecca3" />
            <span>Secure & Private</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Smartphone size={18} color="#4ecca3" />
            <span>Works on Any Device</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Clock size={18} color="#4ecca3" />
            <span>Setup in 5 Minutes</span>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section style={{
        padding: '5rem 2rem',
        background: '#0a0a0a'
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '3rem',
            color: '#fff'
          }}>
            Sound Familiar?
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {painPoints.map((item, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                  gap: '1.5rem',
                  padding: '1.25rem 1.5rem',
                  background: '#151515',
                  borderRadius: '12px',
                  border: '1px solid #222'
                }}
              >
                <div style={{
                  color: '#e74c3c',
                  fontSize: '1rem',
                  textAlign: 'right',
                  textDecoration: 'line-through',
                  opacity: 0.7
                }}>
                  {item.problem}
                </div>
                <ArrowRight size={20} color="#F05A28" style={{ flexShrink: 0 }} />
                <div style={{
                  color: '#4ecca3',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  {item.solution}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" style={{
        padding: '5rem 2rem',
        background: '#111',
        borderTop: '1px solid #1a1a1a'
      }}>
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '2.25rem',
              fontWeight: '700',
              marginBottom: '1rem',
              color: '#fff'
            }}>
              Everything You Need
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#888',
              maxWidth: '500px',
              margin: '0 auto'
            }}>
              Stop juggling 5 different apps. Run your whole operation from one place.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.25rem'
          }}>
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  style={{
                    background: '#1a1a1a',
                    border: '1px solid #252525',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    transition: 'border-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.borderColor = '#F05A28'}
                  onMouseOut={(e) => e.currentTarget.style.borderColor = '#252525'}
                >
                  <div style={{
                    width: '44px',
                    height: '44px',
                    background: 'rgba(240, 90, 40, 0.1)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    <Icon size={22} color="#F05A28" />
                  </div>
                  <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    marginBottom: '0.5rem',
                    color: '#fff'
                  }}>
                    {feature.title}
                  </h3>
                  <p style={{
                    fontSize: '0.95rem',
                    color: '#888',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section style={{
        padding: '5rem 2rem',
        background: '#0a0a0a'
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '3rem',
            color: '#fff'
          }}>
            Lead to Invoice in 4 Steps
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem'
          }}>
            {[
              { num: '1', title: 'Capture', desc: 'Log the lead in 30 seconds' },
              { num: '2', title: 'Quote', desc: 'Send a professional quote' },
              { num: '3', title: 'Schedule', desc: 'Book the job, assign the tech' },
              { num: '4', title: 'Get Paid', desc: 'Invoice and collect payment' }
            ].map((step, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: '#F05A28',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '1.5rem',
                  fontWeight: '800',
                  color: '#fff'
                }}>
                  {step.num}
                </div>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  marginBottom: '0.5rem',
                  color: '#fff'
                }}>
                  {step.title}
                </h3>
                <p style={{
                  fontSize: '0.95rem',
                  color: '#888',
                  margin: 0
                }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section style={{
        padding: '5rem 2rem',
        background: '#111',
        borderTop: '1px solid #1a1a1a'
      }}>
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#fff'
          }}>
            Simple Pricing
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#888',
            marginBottom: '2rem'
          }}>
            Start free. Upgrade when you're ready.
          </p>

          <div style={{
            background: '#1a1a1a',
            border: '2px solid #F05A28',
            borderRadius: '16px',
            padding: '2rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{
              fontSize: '0.9rem',
              color: '#F05A28',
              fontWeight: '600',
              marginBottom: '0.5rem'
            }}>
              MOST POPULAR
            </div>
            <div style={{
              fontSize: '3rem',
              fontWeight: '800',
              color: '#fff',
              marginBottom: '0.5rem'
            }}>
              $50<span style={{ fontSize: '1rem', color: '#888' }}>/month</span>
            </div>
            <div style={{
              color: '#888',
              marginBottom: '1.5rem'
            }}>
              Everything you need to run your business
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem',
              textAlign: 'left',
              marginBottom: '1.5rem'
            }}>
              {[
                'Unlimited customers & leads',
                'Quotes with digital signatures',
                'Job scheduling & calendar',
                'Invoicing & payment tracking',
                'Works on phone, tablet, desktop'
              ].map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  color: '#ccc'
                }}>
                  <CheckCircle size={18} color="#4ecca3" />
                  {item}
                </div>
              ))}
            </div>
            <button
              onClick={onSignup}
              style={{
                width: '100%',
                background: '#F05A28',
                border: 'none',
                color: '#fff',
                padding: '1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '700'
              }}
            >
              Start 14-Day Free Trial
            </button>
          </div>

          <p style={{
            fontSize: '0.9rem',
            color: '#666'
          }}>
            Need more users or features? We have Team and Enterprise plans too.
          </p>
        </div>
      </section>

      {/* Mobile App Section */}
      <section style={{
        padding: '5rem 2rem',
        background: '#0a0a0a'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <Smartphone size={48} color="#F05A28" style={{ marginBottom: '1rem' }} />
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#fff'
          }}>
            Works on Your Phone
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#888',
            marginBottom: '2rem',
            maxWidth: '500px',
            margin: '0 auto 2rem'
          }}>
            No app store needed. Add WorkVan to your home screen and use it like a native app.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            {[
              {
                icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="#3DDC84"><path d="M17.523 2.293l1.182-2.037a.456.456 0 10-.79-.458L16.69 1.882a6.738 6.738 0 00-4.69-1.758c-1.782 0-3.403.673-4.69 1.758L6.085-.202a.456.456 0 10-.79.458l1.182 2.037C4.378 3.852 3.005 6.149 3.005 8.793h17.99c0-2.644-1.373-4.941-3.472-6.5zM8.67 6.124a.91.91 0 110-1.82.91.91 0 010 1.82zm6.66 0a.91.91 0 110-1.82.91.91 0 010 1.82zM3.005 9.704v9.378c0 1.003.812 1.816 1.812 1.816h.907v2.19c0 .503.408.912.91.912s.911-.41.911-.911v-2.19h4.91v2.19c0 .503.407.912.91.912.502 0 .91-.41.91-.911v-2.19h.908c1 0 1.812-.814 1.812-1.817V9.704H3.005zm-1.818 0c-.503 0-.91.408-.91.91v6.377c0 .502.407.91.91.91s.91-.408.91-.91v-6.377c0-.502-.407-.91-.91-.91zm21.626 0c-.502 0-.91.408-.91.91v6.377c0 .502.408.91.91.91s.91-.408.91-.91v-6.377c0-.502-.408-.91-.91-.91z"/></svg>,
                name: 'Android',
                steps: 'Chrome → Menu → Add to Home'
              },
              {
                icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="#ffffff"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>,
                name: 'iPhone',
                steps: 'Safari → Share → Add to Home'
              },
              {
                icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="#888888"><path d="M4 2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6l-2 3-2-3H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm0 2v12h16V4H4zm1 10h14v2H5v-2zm0-3h14v2H5v-2zm0-3h14v2H5V8z"/></svg>,
                name: 'Desktop',
                steps: 'Chrome → Install icon in URL bar'
              }
            ].map((device, idx) => (
              <div key={idx} style={{
                background: '#151515',
                borderRadius: '12px',
                padding: '1.25rem',
                border: '1px solid #222'
              }}>
                <div style={{ marginBottom: '0.5rem' }}>{device.icon}</div>
                <div style={{ fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>{device.name}</div>
                <div style={{ fontSize: '0.85rem', color: '#888' }}>{device.steps}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, #F05A28 0%, #f7931e 100%)'
      }}>
        <div style={{
          maxWidth: '700px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#fff'
          }}>
            Ready to Simplify Your Business?
          </h2>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '2rem'
          }}>
            Join thousands of field service pros who ditched the chaos.
          </p>
          <button
            onClick={onSignup}
            style={{
              background: '#fff',
              border: 'none',
              color: '#F05A28',
              padding: '1.25rem 3rem',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '700',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            Start Your Free Trial
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      {/* Affiliate Section */}
      <section id="affiliate" style={{
        padding: '5rem 2rem',
        background: '#0a0a0a',
        borderTop: '1px solid #1a1a1a'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'rgba(78,204,163,0.1)',
            border: '1px solid rgba(78,204,163,0.3)',
            borderRadius: '100px',
            padding: '0.5rem 1rem',
            marginBottom: '1.5rem',
            fontSize: '0.85rem',
            color: '#4ecca3'
          }}>
            <DollarSign size={16} />
            Affiliate Program
          </div>

          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1rem',
            color: '#fff'
          }}>
            Know Other Service Businesses?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: '#888',
            marginBottom: '2rem',
            maxWidth: '500px',
            margin: '0 auto 2rem'
          }}>
            Refer them to WorkVan and earn <strong style={{ color: '#4ecca3' }}>10% of their subscription</strong> — forever.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {[
              { referrals: '1', earning: '$5/mo' },
              { referrals: '10', earning: '$50/mo' },
              { referrals: '50', earning: '$250/mo' }
            ].map((tier, idx) => (
              <div key={idx} style={{
                background: '#151515',
                borderRadius: '12px',
                padding: '1.25rem',
                border: '1px solid #222'
              }}>
                <div style={{ color: '#888', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                  {tier.referrals} referral{tier.referrals !== '1' ? 's' : ''}
                </div>
                <div style={{ color: '#4ecca3', fontSize: '1.5rem', fontWeight: '700' }}>
                  {tier.earning}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onSignup}
            style={{
              background: '#4ecca3',
              border: 'none',
              color: '#000',
              padding: '1rem 2rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '700'
            }}
          >
            Sign Up & Get Your Referral Link
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        background: '#0a0a0a',
        borderTop: '1px solid #1a1a1a',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '1000px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <a
            href="/"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <img
              src="/WorkVan-logo-dark.png"
              alt="Work Van"
              style={{ height: '28px', width: 'auto' }}
            />
            <span style={{ color: '#666', fontWeight: '600' }}>WorkVan</span>
          </a>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
          }}>
            <a
              href="/terms"
              style={{
                color: '#666',
                fontSize: '0.85rem',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.target.style.color = '#F05A28'}
              onMouseLeave={(e) => e.target.style.color = '#666'}
            >
              Terms & Conditions
            </a>
            <a
              href="/privacy"
              style={{
                color: '#666',
                fontSize: '0.85rem',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => e.target.style.color = '#F05A28'}
              onMouseLeave={(e) => e.target.style.color = '#666'}
            >
              Privacy Policy
            </a>
            <span style={{
              color: '#555',
              fontSize: '0.85rem'
            }}>
              © 2026 WorkVan. All rights reserved.
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
